import toast from "react-hot-toast";

export interface ValidationResult {
  isValid: boolean;
  message?: string;
}

export interface ValidationField {
  type: string;
  fieldName: string;
  displayName?: string;
  required: boolean;
  file?: any[];
}

/**
 * Validates a single form field based on type and requirements
 *
 * @param fieldType - Type of field (email, phone, short_text, etc.)
 * @param fieldValue - Current value of the field
 * @param isRequired - Whether the field is required
 * @param fileData - Optional file array for file_upload fields
 * @param displayName - Optional display name for better error messages
 * @returns ValidationResult with isValid flag and optional error message
 */
export const validateField = (
  fieldType: string,
  fieldValue: any,
  isRequired: boolean,
  fileData?: any[],
  displayName?: string
): ValidationResult => {
  // Create user-friendly field name for error messages
  const fieldLabel = displayName || "This field";

  // Skip validation for non-required fields that are empty
  if (!isRequired && (!fieldValue || fieldValue.length === 0)) {
    return { isValid: true };
  }

  // Required field validation
  if (isRequired) {
    if (fieldType === "multiple_options" && Array.isArray(fieldValue)) {
      if (fieldValue.length === 0) {
        return { isValid: false, message: `${fieldLabel}: Please select an option` };
      }
    } else if (fieldType === "file_upload") {
      if (!fileData || fileData.length === 0) {
        return { isValid: false, message: `${fieldLabel}: Please upload a file` };
      }
    } else if (fieldType === "payment") {
      // Payment fields don't need validation here
      return { isValid: true };
    } else if (!fieldValue || fieldValue.toString().trim().length === 0) {
      return { isValid: false, message: `${fieldLabel} is required` };
    }
  }

  // Format validation (only if field has value)
  if (fieldValue && fieldValue.toString().trim().length > 0) {
    if (fieldType === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const trimmedEmail = fieldValue.toString().trim();
      if (!emailRegex.test(trimmedEmail)) {
        return { isValid: false, message: `${fieldLabel}: Please enter a valid email address` };
      }
    }

    if (fieldType === "phone") {
      // Basic phone validation - at least 10 digits
      const cleanPhone = fieldValue.replace(/\D/g, '');
      if (cleanPhone.length < 10) {
        return { isValid: false, message: `${fieldLabel}: Please enter a valid phone number` };
      }
    }
  }

  return { isValid: true };
};

/**
 * Validates a form field object and shows toast error if invalid
 *
 * @param field - Field object containing type, fieldName, displayName, required, file
 * @param formData - Current form data object with all field values
 * @returns boolean - true if valid, false if invalid
 */
export const validateFormField = (
  field: ValidationField,
  formData: any
): boolean => {
  const fieldValue = formData[field.fieldName];
  const fileData = field.file;

  const result = validateField(
    field.type,
    fieldValue,
    field.required,
    fileData,
    field.displayName  // Pass displayName for better error messages
  );

  if (!result.isValid && result.message) {
    toast.error(result.message);
  }

  return result.isValid;
};

/**
 * Email-specific validation
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Phone-specific validation
 */
export const validatePhone = (phone: string): boolean => {
  const cleanPhone = phone.replace(/\D/g, '');
  return cleanPhone.length >= 10;
};

/**
 * Checks if a form already has an email field (including in group fields)
 * @param formStructure - The form structure array
 * @param formData - Current form data with values
 * @returns true if email field exists and has a value
 */
export const hasEmailInForm = (formStructure: any[], formData: any): boolean => {
  if (!formStructure || !formData) return false;

  for (const question of formStructure) {
    if (!question.fields) continue;

    for (const field of question.fields) {
      // Check regular email field
      if (field.type === 'email' && formData[field.fieldName]) {
        return true;
      }

      // Check group fields for email
      if (field.type === 'group_field' && field.groupFields && Array.isArray(field.groupFields)) {
        for (const nestedField of field.groupFields) {
          if (nestedField.type === 'email' && formData[nestedField.fieldName]) {
            return true;
          }
        }
      }
    }
  }

  return false;
};

/**
 * Checks if a form already has a phone field (including in group fields)
 * @param formStructure - The form structure array
 * @param formData - Current form data with values
 * @returns true if phone field exists and has a value
 */
export const hasPhoneInForm = (formStructure: any[], formData: any): boolean => {
  if (!formStructure || !formData) return false;

  for (const question of formStructure) {
    if (!question.fields) continue;

    for (const field of question.fields) {
      // Check regular phone field
      if (field.type === 'phone' && formData[field.fieldName]) {
        return true;
      }

      // Check group fields for phone
      if (field.type === 'group_field' && field.groupFields && Array.isArray(field.groupFields)) {
        for (const nestedField of field.groupFields) {
          if (nestedField.type === 'phone' && formData[nestedField.fieldName]) {
            return true;
          }
        }
      }
    }
  }

  return false;
};
